<template>
  <div>
    <!-- SVG Canvas -->
    <svg
      ref="svgCanvas"
      :class="penToolActive ? 'w-full h-full cursor-crosshair min-w-0 min-h-0' : dragHandActive ? 'w-full h-full cursor-grab min-w-0 min-h-0' : 'w-full h-full cursor-default min-w-0 min-h-0'"
      :viewBox="`0 0 ${svgWidth} ${svgHeight}`"
      preserveAspectRatio="xMidYMid meet"
      style="will-change: transform;"
      @mousedown="downAt = [$event.clientX, $event.clientY]; $emit('mousedown', $event)"
      @mousemove="$emit('mousemove', $event)"
      @mouseup="$emit('mouseup', $event)"
      @mouseleave="$emit('mouseup', $event)"
      @wheel="$emit('wheel', $event)"
    >
      <!-- Transform Group for Zoom and Pan -->
      <g :transform="`translate(${panX}, ${panY}) scale(${zoomLevel})`" style="will-change: transform;">
        <!-- Map Background -->
        <image
          v-if="mapImageSrc"
          :href="mapImageSrc"
          :x="mapConfig.x"
          :y="mapConfig.y"
          :width="mapConfig.width"
          :height="mapConfig.height"
          @click="$emit('toggleMaps')"
        />

        <!-- Players (sorted by Z for proper layering). Selectable where the page listens (v5) -->
        <g
          v-for="player in sortedPlayers"
          :key="`player-${player.steamID}`"
          :class="{ 'vc-pickable': selectable && !penToolActive }"
          @click="pick(player, $event)"
        >
          <!-- Selected: a white ring (the radar is dark in both themes) -->
          <circle
            v-if="player.steamID === selectedSteamId"
            :cx="player.x"
            :cy="player.y"
            :r="playerRadius * 1.55"
            fill="none"
            stroke="#fff"
            :stroke-width="playerRadius * 0.22"
          />
          <!-- Player Shadow (depth) -->
          <circle
            :cx="player.x + playerRadius * 0.12"
            :cy="player.y + playerRadius * 0.12"
            :r="playerRadius * 1.05"
            fill="black"
            :opacity="0.25 * player.opacity"
          />

          <!-- Player Circle -->
          <circle
            :cx="player.x"
            :cy="player.y"
            :r="playerRadius"
            :fill="player.color"
            :opacity="player.opacity"
          />

          <!-- Player Circle Outline (helps on bright map areas) -->
          <circle
            :cx="player.x"
            :cy="player.y"
            :r="playerRadius"
            fill="none"
            stroke="rgba(0,0,0,0.55)"
            :stroke-width="playerRadius * 0.15"
            :opacity="player.opacity"
          />

          <!-- Carried bomb: a red pip on the carrier -->
          <circle
            v-if="renderedBomb?.carrierSteamID === player.steamID"
            :cx="player.x + playerRadius * 0.7"
            :cy="player.y + playerRadius * 0.7"
            :r="playerRadius * 0.42"
            :fill="renderedBomb.color"
            stroke="rgba(0,0,0,0.7)"
            :stroke-width="playerRadius * 0.12"
          />

          <!-- Blinded indicator (outer ring) -->
          <circle
            v-if="player.isBlinded"
            :cx="player.x"
            :cy="player.y"
            :r="playerRadius + blindedRingExtra"
            fill="none"
            :stroke="player.color"
            :stroke-width="blindedRingStrokeWidth"
            :opacity="player.opacity * 0.8"
          />

          <!-- View Angle Triangle Shadow -->
          <path
            v-if="!player.isDead && player.viewAngle !== undefined"
            :d="getTrianglePoints(player)"
            fill="black"
            :opacity="0.25 * player.opacity"
            :transform="`${getTriangleTransform(player)} translate(${playerRadius * 0.12} ${playerRadius * 0.12})`"
          />

          <!-- View Angle Triangle -->
          <path
            v-if="!player.isDead && player.viewAngle !== undefined"
            :d="getTrianglePoints(player)"
            :fill="player.color"
            :opacity="player.opacity"
            :transform="getTriangleTransform(player)"
          />

          <!-- View Angle Triangle Outline -->
          <path
            v-if="!player.isDead && player.viewAngle !== undefined"
            :d="getTrianglePoints(player)"
            fill="none"
            stroke="rgba(0,0,0,0.55)"
            :stroke-width="playerRadius * 0.12"
            :opacity="player.opacity"
            :transform="getTriangleTransform(player)"
          />

          <!-- Player Name Background -->
          <rect
            v-if="!player.isDead"
            :x="player.x - (player.nameWidth || nameBgHeight) / 2"
            :y="player.y - nameBgYOffset"
            :width="player.nameWidth || nameBgHeight"
            :height="nameBgHeight"
            :fill="'rgba(0,0,0,0.6)'"
            :opacity="player.opacity * 0.6"
            :rx="nameBgRx"
          />

          <!-- Player Name Text -->
          <text
            v-if="!player.isDead"
            :x="player.x"
            :y="player.y - nameTextYOffset"
            :fill="player.nameColor || 'white'"
            :opacity="player.opacity"
            text-anchor="middle"
            :font-size="nameFontSize"
            :font-weight="player.steamID === selectedSteamId ? 800 : 700"
          >
            {{ player.name }}
          </text>

          <!-- Weapon Icon (above head) -->
          <image
            v-if="!player.isDead && player.weaponIcon"
            :href="player.weaponIcon"
            :x="player.x - weaponIconOffsetX"
            :y="player.y - weaponIconOffsetY"
            :width="weaponIconSize"
            :height="weaponIconSize"
            :opacity="player.opacity * 0.8"
          />
        </g>

        <!-- Grenade Projectiles (sorted by Z) -->
        <g v-for="nade in sortedNadesByZ" :key="`nade-${nade.uniqueID}`">
          <!-- Nade Trail -->
          <polyline
            v-if="nade.trail && nade.trail.length > 1"
            :points="nade.trail.map(p => `${p.x},${p.y}`).join(' ')"
            :stroke="nade.color"
            :stroke-width="nadeTrailStrokeWidth"
            :stroke-dasharray="nadeTrailDashArray"
            fill="none"
            :opacity="nade.opacity * 0.6"
          />

          <!-- Nade Icon - Always show, fallback to circle if icon missing -->
          <g v-if="nade.icon">
            <!-- Team-colored background circle -->
            <circle
              :cx="nade.x"
              :cy="nade.y"
              :r="nadeBgRadius * (nade.heightScale || 1)"
              :fill="nade.color"
              :opacity="nade.opacity * 0.3"
            />
            <image
              :href="nade.icon"
              :x="nade.x - (nadeIconSize / 2) * (nade.heightScale || 1)"
              :y="nade.y - (nadeIconSize / 2) * (nade.heightScale || 1)"
              :width="nadeIconSize * (nade.heightScale || 1)"
              :height="nadeIconSize * (nade.heightScale || 1)"
              :opacity="nade.opacity"
            />
          </g>
          <g v-else>
            <!-- Fallback: Circle if icon doesn't exist -->
            <circle
              :cx="nade.x"
              :cy="nade.y"
              :r="nadeFallbackRadius * (nade.heightScale || 1)"
              :fill="nade.color"
              :opacity="nade.opacity"
              stroke="#000000"
              :stroke-width="nadeFallbackStrokeWidth * (nade.heightScale || 1)"
            />
          </g>
        </g>

        <!-- Bomb, dropped or planted (a carried one is the pip on its carrier) -->
        <g v-if="renderedBomb && !renderedBomb.carrierSteamID">
          <circle
            v-if="renderedBomb.isPlanted"
            class="vc-plant"
            :cx="renderedBomb.x"
            :cy="renderedBomb.y"
            :r="playerRadius * 1.3"
            fill="none"
            :stroke="renderedBomb.color"
            :stroke-width="playerRadius * 0.15"
          />
          <path
            :d="bombPathData"
            :transform="bombTransform"
            :fill="renderedBomb.color"
            :opacity="renderedBomb.opacity"
            stroke="rgba(0,0,0,0.7)"
            :stroke-width="80"
          />
          <!-- Defuse Progress Bar -->
          <g v-if="renderedBomb.isDefusing && renderedBomb.defuseProgress > 0">
            <rect
              :x="renderedBomb.x - bombDefuseOffsetX"
              :y="renderedBomb.y - bombDefuseOffsetY"
              :width="bombDefuseWidth"
              :height="bombDefuseHeight"
              fill="rgba(255, 255, 255, 0.3)"
              :rx="bombDefuseRx"
            />
            <rect
              :x="renderedBomb.x - bombDefuseOffsetX"
              :y="renderedBomb.y - bombDefuseOffsetY"
              :width="bombDefuseWidth * renderedBomb.defuseProgress"
              :height="bombDefuseHeight"
              fill="#14B8A6"
              :rx="bombDefuseRx"
            />
          </g>
        </g>

        <!-- Infernos (Fire) -->
        <g v-for="fire in renderedFires" :key="`fire-${fire.uniqueID}`">
          <polygon
            v-if="fire.points.length > 2"
            :points="fire.points.map(p => `${p.x},${p.y}`).join(' ')"
            fill="red"
            stroke="black"
            :stroke-width="fireStrokeWidth"
            :opacity="fire.opacity"
          />
          <text
            :x="fire.textX"
            :y="fire.textY"
            fill="white"
            :opacity="fire.opacity * 0.6"
            text-anchor="middle"
            :font-size="fireTextFontSize"
          >
            {{ fire.timer }}
          </text>
        </g>

        <!-- Smoke Effects -->
        <g v-for="smoke in renderedSmokes" :key="`smoke-${smoke.uniqueID}`">
          <circle
            :cx="smoke.x"
            :cy="smoke.y"
            :r="smoke.radius"
            :fill="smoke.color"
            :opacity="smoke.opacity"
            stroke="black"
            :stroke-width="smokeStrokeWidth"
          />
          <text
            :x="smoke.x"
            :y="smoke.y"
            fill="white"
            :opacity="smoke.opacity * 0.6"
            text-anchor="middle"
            :font-size="smokeTextFontSize"
          >
            {{ smoke.timer }}
          </text>
        </g>

        <!-- Damage Numbers -->
        <text
          v-for="damage in renderedDamages"
          :key="damage.id"
          :x="damage.x"
          :y="damage.y"
          fill="white"
          :opacity="damage.opacity"
          text-anchor="middle"
          :font-size="damage.fontSize"
          font-weight="bold"
        >
          {{ damage.amount }}
        </text>

        <!-- Weapon Fires (matching old Konva implementation) -->
        <g v-for="fire in renderedWeaponFires" :key="fire.id">
          <!-- Circle (orange, animated scale) -->
          <circle
            :cx="fire.circleX"
            :cy="fire.circleY"
            :r="fire.radius * fire.scale"
            fill="orange"
            :opacity="fire.opacity"
          />
          <!-- Rect (white projectile, animated height) -->
          <rect
            :x="fire.rectX"
            :y="fire.rectY"
            :width="fire.rectWidth"
            :height="fire.currentHeight"
            fill="white"
            :opacity="fire.rectOpacity"
            :transform="`rotate(${fire.angle} ${fire.rectX} ${fire.rectY})`"
          />
        </g>

        <!-- Explosions (HE and Flash) -->
        <g v-for="explosion in renderedExplosions" :key="explosion.id">
          <circle
            :cx="explosion.x"
            :cy="explosion.y"
            :r="explosion.baseRadius * explosion.scale"
            :fill="explosion.color"
            :opacity="explosion.opacity"
          />
        </g>
      </g>
    </svg>

    <!-- Pen Tool Drawing Overlay - Matches main SVG coordinate system -->
    <svg
      v-if="penToolActive || drawnPaths.length > 0"
      ref="drawingOverlay"
      class="absolute inset-0 w-full h-full pointer-events-none min-w-0 min-h-0"
      :class="{ 'pointer-events-auto cursor-crosshair': penToolActive }"
      :viewBox="`0 0 ${svgWidth} ${svgHeight}`"
      preserveAspectRatio="xMidYMid meet"
      style="z-index: 10; will-change: transform;"
      @mousedown="$emit('drawingMousedown', $event)"
      @mousemove="$emit('drawingMousemove', $event)"
      @mouseup="$emit('drawingMouseup', $event)"
      @mouseleave="$emit('drawingMouseup', $event)"
      @wheel="$emit('wheel', $event)"
    >
      <!-- Transform Group matching the main SVG for zoom/pan -->
      <g :transform="`translate(${panX}, ${panY}) scale(${zoomLevel})`">
        <!-- Drawn Paths -->
        <path
          v-for="(path, index) in drawnPaths"
          :key="`drawing-${index}`"
          :d="path.d"
          :stroke="path.stroke"
          :stroke-width="path.strokeWidth"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <!-- Current Path Being Drawn -->
        <path
          v-if="currentPath"
          :d="currentPath"
          :stroke="penColor"
          :stroke-width="penStrokeWidth"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </g>
    </svg>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { BOMB_CENTRE } from '../../utils/demoViewer/bombRenderer';

const props = defineProps({
  svgWidth: Number,
  svgHeight: Number,
  mapImageSrc: String,
  mapConfig: Object,
  panX: Number,
  panY: Number,
  zoomLevel: Number,
  penToolActive: Boolean,
  dragHandActive: Boolean,
  drawnPaths: Array,
  currentPath: String,
  penColor: String,
  penStrokeWidth: Number,
  sortedPlayers: Array,
  sortedNadesByZ: Array,
  renderedBomb: Object,
  renderedFires: Array,
  renderedSmokes: Array,
  renderedDamages: Array,
  renderedWeaponFires: Array,
  renderedExplosions: Array,
  playerRadius: Number,
  blindedRingExtra: Number,
  blindedRingStrokeWidth: Number,
  bombScale: Number,
  bombPathData: String,
  nameFontSize: Number,
  nameBgHeight: Number,
  nameBgYOffset: Number,
  nameTextYOffset: Number,
  nameBgRx: Number,
  weaponIconSize: Number,
  weaponIconOffsetX: Number,
  weaponIconOffsetY: Number,
  nadeTrailStrokeWidth: Number,
  nadeTrailDashArray: String,
  nadeBgRadius: Number,
  nadeIconSize: Number,
  nadeFallbackRadius: Number,
  nadeFallbackStrokeWidth: Number,
  fireStrokeWidth: Number,
  fireTextFontSize: Number,
  smokeStrokeWidth: Number,
  smokeTextFontSize: Number,
  bombDefuseWidth: Number,
  bombDefuseHeight: Number,
  bombDefuseOffsetX: Number,
  bombDefuseOffsetY: Number,
  bombDefuseRx: Number,
  getTrianglePoints: Function,
  getTriangleTransform: Function,
  selectable: Boolean,
  selectedSteamId: { type: [String, Number], default: null },
});

// Centred on the bomb's position: the path's own origin is its top left corner
const bombTransform = computed(() => {
  const b = props.renderedBomb;
  return `translate(${b.x},${b.y}) scale(${props.bombScale}) translate(${-BOMB_CENTRE[0]},${-BOMB_CENTRE[1]})`;
});

// A click that ends a pan is not a pick: the dot moved with the cursor, so mouseup lands on it
const downAt = ref(null);
const emit = defineEmits(['mousedown', 'mousemove', 'mouseup', 'wheel', 'drawingMousedown', 'drawingMousemove', 'drawingMouseup', 'toggleMaps', 'select']);
function pick(player, e) {
  if (!props.selectable || props.penToolActive) return;
  const [x, y] = downAt.value ?? [e.clientX, e.clientY];
  if (Math.hypot(e.clientX - x, e.clientY - y) > 4) return;
  emit('select', player.steamID);
}

const svgCanvas = ref(null);
const drawingOverlay = ref(null);

defineExpose({
  svgCanvas,
  drawingOverlay,
});

</script>

<style scoped>
.vc-pickable { cursor: pointer; }
/* Planted bomb: a ring pulsing out from it */
.vc-plant { transform-box: fill-box; transform-origin: center; animation: vc-plant 1.2s ease-out infinite; }
@keyframes vc-plant { from { transform: scale(0.6); opacity: 0.9; } to { transform: scale(1.4); opacity: 0; } }
@media (prefers-reduced-motion: reduce) { .vc-plant { animation: none; opacity: 0.8; } }
</style>
